import React, { useEffect, useRef, useState } from "react";
import {
  Box, Container, Flex, Text, Icon, useColorModeValue, HStack,
} from "@chakra-ui/react";
import { motion, useInView } from "framer-motion";
import {
  FaUsers, FaChalkboardTeacher, FaRocket, FaGlobeAmericas,
} from "react-icons/fa";

const MotionBox  = motion(Box);

/* ── animated count-up hook ── */
function useCountUp(target, inView, duration = 1.8) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / (duration * 1000), 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(step);
      else setDisplay(target);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [inView, target, duration]);

  return display;
}

/* ── stat item ── */
const StatItem = ({ icon, value, suffix, label, color, delay, inView }) => {
  const count = useCountUp(value, inView, 1.6);
  const borderCol = useColorModeValue("gray.100", "whiteAlpha.100");
  const mutedColor = useColorModeValue("gray.500", "gray.500");

  return (
    <MotionBox
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      flex={1}
      minW={{ base: "140px", md: "0" }}
      position="relative"
      px={{ base: 4, md: 8 }}
      py={{ base: 6, md: 0 }}
      _notLast={{
        _after: {
          content: '""',
          position: "absolute",
          right: 0,
          top: "10%",
          height: "80%",
          width: "1px",
          bg: borderCol,
          display: { base: "none", md: "block" },
        },
      }}
    >
      {/* icon */}
      <HStack spacing={2} mb={3}>
        <Box
          w="28px" h="28px" rounded="md"
          bg={`${color}18`}
          display="flex" alignItems="center" justifyContent="center"
        >
          <Icon as={icon} color={color} boxSize={3.5} />
        </Box>
        <Text
          fontSize="9px"
          fontWeight="800"
          letterSpacing="0.2em"
          textTransform="uppercase"
          color={mutedColor}
        >
          {label}
        </Text>
      </HStack>

      {/* big number */}
      <HStack spacing={0} align="flex-end" lineHeight={1}>
        <Text
          fontSize={{ base: "56px", md: "72px", lg: "88px" }}
          fontWeight="900"
          fontFamily="Bricolage Grotesque, sans-serif"
          lineHeight={1}
          letterSpacing="-4px"
          color={useColorModeValue("gray.900", "white")}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {count.toLocaleString()}
        </Text>
        <Text
          fontSize={{ base: "32px", md: "40px", lg: "48px" }}
          fontWeight="900"
          fontFamily="Bricolage Grotesque, sans-serif"
          lineHeight={1.1}
          letterSpacing="-2px"
          color={color}
          pb="6px"
        >
          {suffix}
        </Text>
      </HStack>

      {/* underline accent */}
      <MotionBox
        initial={{ scaleX: 0 }}
        animate={inView ? { scaleX: 1 } : {}}
        transition={{ duration: 0.6, delay: delay + 0.4, ease: "easeOut" }}
        h="3px"
        w="48px"
        bg={color}
        rounded="full"
        mt={3}
        style={{ originX: 0 }}
        opacity={0.7}
      />
    </MotionBox>
  );
};

/* ══════════════════════════════════════════
   STATS SECTION
══════════════════════════════════════════ */
const statsData = [
  { icon: FaUsers,            value: 6000, suffix: "+",  label: "Learners Trusted Us",  color: "#028EC4" },
  { icon: FaChalkboardTeacher,value: 20,  suffix: "+",  label: "Mentor Network",   color: "#7C3AED" },
  { icon: FaRocket,           value: 74,   suffix: "%",  label: "Placement Rate",   color: "#059669" },
  { icon: FaGlobeAmericas,    value: 10,   suffix: "+",  label: "Global Partners",  color: "#D97706" },
];

const StatsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const bg = useColorModeValue("#FDFDFD", "#05060b");

  return (
    <Box ref={ref} bg={bg} borderY="1px solid" borderColor={borderColor}>
      <Container maxW="7xl" py={{ base: 10, md: 14 }}>
        <Flex
          direction={{ base: "row", md: "row" }}
          flexWrap={{ base: "wrap", md: "nowrap" }}
          justify="space-between"
          align="stretch"
          gap={{ base: 0, md: 0 }}
        >
          {statsData.map((s, i) => (
            <StatItem key={i} {...s} delay={i * 0.12} inView={inView} />
          ))}
        </Flex>
      </Container>
    </Box>
  );
};

export default StatsSection;